import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react'

type SetValue<T> = Dispatch<SetStateAction<T>>

/**
 * Hook para persistir estado en localStorage con sincronización entre pestañas
 * @param key - Clave para localStorage
 * @param initialValue - Valor inicial si no existe en localStorage
 * @returns [value, setValue] - Similar a useState pero persistido
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
  // Función para leer del localStorage
  const readValue = useCallback((): T => {
    // Prevenir error en SSR
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  }, [initialValue, key])

  // Estado con valor inicial desde localStorage
  const [storedValue, setStoredValue] = useState<T>(readValue)

  // Retornar una versión wrapped de useState's setter function que persiste el nuevo valor en localStorage
  const setValue: SetValue<T> = useCallback(
    (value) => {
      // Prevenir error en SSR
      if (typeof window === 'undefined') {
        console.warn(
          `Tried setting localStorage key "${key}" even though environment is not a client`
        )
      }

      try {
        // Permitir que value sea una función como en useState
        const newValue = value instanceof Function ? value(storedValue) : value

        // Guardar en localStorage
        window.localStorage.setItem(key, JSON.stringify(newValue))

        // Guardar estado
        setStoredValue(newValue)

        // Disparar evento personalizado para sincronizar entre componentes
        window.dispatchEvent(
          new CustomEvent('local-storage', {
            detail: { key, newValue }
          })
        )
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error)
      }
    },
    [key, storedValue]
  )

  // Escuchar cambios en localStorage desde otras pestañas
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== key || e.storageArea !== localStorage) {
        return
      }

      try {
        if (e.newValue !== null) {
          setStoredValue(JSON.parse(e.newValue))
        }
      } catch (error) {
        console.warn(`Error parsing localStorage value for key "${key}":`, error)
      }
    }

    // Escuchar cambios desde otras pestañas
    window.addEventListener('storage', handleStorageChange)

    // Escuchar cambios desde la misma pestaña (evento personalizado)
    const handleLocalStorageChange = (e: CustomEvent) => {
      if (e.detail.key !== key) {
        return
      }
      setStoredValue(e.detail.newValue)
    }

    window.addEventListener('local-storage', handleLocalStorageChange as EventListener)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('local-storage', handleLocalStorageChange as EventListener)
    }
  }, [key])

  // Sincronizar con localStorage cuando el key cambia
  useEffect(() => {
    setStoredValue(readValue())
  }, [key])

  return [storedValue, setValue]
}

/**
 * Hook para eliminar un valor de localStorage
 * @param key - Clave para eliminar de localStorage
 */
export function useLocalStorageDelete(key: string): () => void {
  return useCallback(() => {
    try {
      window.localStorage.removeItem(key)

      // Disparar evento para sincronización
      window.dispatchEvent(
        new CustomEvent('local-storage', {
          detail: { key, newValue: undefined }
        })
      )
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error)
    }
  }, [key])
}

/**
 * Hook para obtener todas las claves de localStorage con un prefijo
 * @param prefix - Prefijo para filtrar claves
 * @returns Array de claves que coinciden con el prefijo
 */
export function useLocalStorageKeys(prefix: string): string[] {
  const [keys, setKeys] = useState<string[]>([])

  useEffect(() => {
    const updateKeys = () => {
      if (typeof window === 'undefined') return

      try {
        const allKeys = Object.keys(window.localStorage)
        const filteredKeys = allKeys.filter((key) => key.startsWith(prefix))
        setKeys(filteredKeys)
      } catch (error) {
        console.warn('Error reading localStorage keys:', error)
        setKeys([])
      }
    }

    updateKeys()

    // Actualizar cuando cambie localStorage
    const handleStorageChange = () => updateKeys()
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('local-storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('local-storage', handleStorageChange)
    }
  }, [prefix])

  return keys
}
